import React, { useCallback, useEffect } from "react";
import { Asset } from "src/types/assets";
import { Event } from "src/types/conversations";
import { FileService } from "src/services/file.service";
import { useDispatch } from "react-redux";
import { fetchPreviewAsset } from "src/redux/actions/assets";

import Tooltip from "src/components/tooltip";
import Thumbnail from "src/components/thumbnail";
import dateformat from "dateformat";
import InlinePageAnalytics from "src/components/analytics/page/InlineAnalytics";
import VideoAnalytics from "src/components/analytics/video";
import { Col, Row } from "react-bootstrap";
import AudioAnalytics from "src/components/analytics/audio";
import { Link } from "react-router-dom";

import styles from "./assetShare.module.scss";
import _ from "lodash";
import cx from "classnames";
import Text from "src/components/text";

interface IAssetShare {
  asset: Asset;
  fetchAnalytics: (assetId: string) => void;
  shareId?: string;
  sectionId?: string;
  source: "contact" | "conversation";
}

const AssetShare: React.FC<IAssetShare> = ({
  asset,
  fetchAnalytics,
  shareId,
  sectionId,
  source,
}) => {
  //constants
  const hasAnalytics = FileService.hasAnalytics(asset.metadata);
  const dispatch = useDispatch();
  const [showTooltip, setShowTooltip] = React.useState(false);

  let assetViewEvent: Event | null = null;
  let downloadViewEvent: Event | null = null;

  if (asset.events) {
    asset.events.forEach((event) => {
      switch (event.type) {
        case "asset_viewed":
          assetViewEvent = event;
          break;
        case "asset_download":
          downloadViewEvent = event;
          break;
        default:
          break;
      }
    });
  }

  //side effects
  useEffect(() => {
    if (hasAnalytics && !asset.fetchedAnalytics) {
      fetchAnalytics(asset.id);
    }
  }, []);

  useEffect(() => {
    if (
      FileService.hasPageAnalytics(asset.metadata) &&
      asset.fetchedAnalytics
    ) {
      if (!asset.preview) {
        dispatch(
          fetchPreviewAsset({
            id: asset.id,
            shareId,
            sectionId,
          }),
        );
      }
    }
  }, [asset.fetchedAnalytics]);

  //functions
  const generateTimeSpent = useCallback(() => {
    const timeSpent = _.get(asset, "totalTimeSpent", 0);
    if (timeSpent >= 60) {
      return `~ ${Math.round(timeSpent / 60)}m`;
    } else {
      return `~ ${Math.round(timeSpent)}s`;
    }
  }, [asset]);

  const renderAnalyticsChart = () => {
    const analyticsType = _.get(asset, "analytics.type");
    switch (analyticsType) {
      case "PAGE_ANALYTICS":
        return (
          <InlinePageAnalytics
            analytics={asset.analytics}
            preview={asset.preview}
          />
        );
      case "VIDEO_ANALYTICS":
        return (
          <VideoAnalytics
            analytics={asset.analytics}
            metadata={asset.metadata}
            width={200}
            height={70}
          />
        );

      case "AUDIO_ANALYTICS":
        return (
          <AudioAnalytics
            analytics={asset.analytics}
            metadata={asset.metadata}
            width={200}
            height={70}
          />
        );
      default:
        break;
    }
  };

  //render
  return (
    <Row className={styles.container}>
      <Col
        xl={source === "contact" ? 7 : 9}
      >
        <Link to={`/assets/${asset.id}`} className={styles.assetShareColumn}>
          <div className={styles.imageContainer}>
            <Thumbnail
              media={asset.icon}
              defaultImage="/images/assets/default_asset.jpg"
              className={styles.icon}
            />
          </div>

          <div className={styles.content}>
            <Text className={cx(styles.name)} text={asset.name} lines={1} />
            <div className={styles.analytics}>
              <Tooltip
                placement="top"
                content={
                  assetViewEvent
                    ? dateformat(
                        _.get(assetViewEvent, "createdDate"),
                        "mmmm dd, yyyy",
                      )
                    : "No views"
                }
              >
                <strong>{_.get(assetViewEvent, "count", 0)}</strong>
                <span>Views</span>
              </Tooltip>

              <Tooltip
                placement="top"
                content={
                  downloadViewEvent
                    ? dateformat(
                        _.get(downloadViewEvent, "createdDate"),
                        "mmmm dd, yyyy",
                      )
                    : "No downloads"
                }
              >
                <strong>{_.get(downloadViewEvent, "count", 0)}</strong>
                <span>Downloads</span>
              </Tooltip>
              <div>
                <strong>{generateTimeSpent()}</strong>
                <span>Time spent</span>
              </div>
            </div>
          </div>
          {source === "conversation" && hasAnalytics && assetViewEvent &&(
            <div className={styles.lastViewed}>
              last viewed on{" "}
              {dateformat(
                _.get(assetViewEvent, "createdDate"),
                "mmmm dd, yyyy",
              )}
            </div>
          )}
        </Link>
      </Col>
      <Col
        xl={source === "contact" ? 5 : 3}
        className={cx(styles.assetShareColumn, styles.chart, {
          [styles.pageAnalyticsWrapper]:
            _.get(asset, "analytics.type") === "PAGE_ANALYTICS" &&
            hasAnalytics &&
            assetViewEvent,
          [styles.conversationAnalyticsWrapper]: source === "conversation",
        })}
      >
        {_.get(asset, "fetchingAnalytics") && (
          <span className={styles.message}>loading analytics</span>
        )}
        {hasAnalytics &&
          !assetViewEvent &&
          !_.get(asset, "fetchingAnalytics") && (
            <span className={styles.message}>No activity recorded</span>
          )}
        {asset.analytics &&
          generateTimeSpent() !== `~ 0s` &&
          assetViewEvent &&
          renderAnalyticsChart()}
      </Col>
    </Row>
  );
};

export default AssetShare;
